import { ref, computed, watch } from 'vue';
import type { Ref } from 'vue';
import { useProductStore } from '@/store/product.store';
import { CategorieMutation } from '@/mutations/categorie.mutations';
import { idCategory } from '@/refs/categories.refs';
import { idToDelete, organizationIdCommerce } from '@/refs/commerce.refs';
import { category_product_filter } from '@/refs/product.refs';
import axiosInstance from '@/services/interceptor.services';
import { useAutoTranslate } from './useAutoTranslate';

export type TCategoryModule =
  'click_and_collect' | 'kiosk' | 'pds_display' | 'pos_register';

export const useCatalogueCollections = (
  dataGetCategories: Ref<any>,
  dataGetProduct: Ref<any>,
  refetchProduct: () => void,
  refetchCategories: () => void,
  showSuccessToast: (msg: string) => void,
  showErrorToast: (msg: string) => void,
  refetchProductList?: () => void,
) => {
  const refetchBoth = () => {
    refetchProduct();
    refetchProductList?.();
  };
  const productStore = useProductStore();
  const {
    handleCreateCategory,
    handleUpdateCategoryById,
    handleDeleteCategorie,
  } = CategorieMutation();
  const { runTranslation } = useAutoTranslate();

  const deletedCollections = ref<string[]>([]);
  const localCollections = ref<{ name: string; products: any[] }[]>([]);
  const editingSection = ref<string | null>(null);
  const editingName = ref('');
  const showAddInput = ref(false);
  const newCollectionName = ref('');
  const newColFr = ref('');
  const newColNl = ref('');
  const newColEn = ref('');
  const newColLang = ref<'fr' | 'nl' | 'en'>('fr');
  const newColModules = ref<TCategoryModule[]>([]);
  const isAutoTranslatingNewCol = ref(false);
  const confirmDeleteName = ref<string | null>(null);
  const confirmDeleteId = ref<string | null>(null);
  const draggingProduct = ref<any>(null);
  const dragOverCollection = ref<string | null>(null);
  const droppedProducts = ref<Record<string, any[]>>({});
  const expandedSections = ref<string[]>([]);
  const showCollections = ref(false);
  const localCategoryOrder = ref<string[] | null>(null);
  const movingCategoryId = ref<string | null>(null);
  const deletingProductId = ref<string | null>(null);
  const isAddingCollection = ref(false);
  const editCatLang = ref<'fr' | 'nl' | 'en'>('fr');
  const editCatDesigFr = ref('');
  const editCatDesigNl = ref('');
  const editCatDesigEn = ref('');
  const editCatDescFr = ref('');
  const editCatDescNl = ref('');
  const editCatDescEn = ref('');
  const editCatModules = ref<TCategoryModule[]>([]);
  const isSavingCatEdit = ref(false);
  const isAutoTranslatingCatDesig = ref(false);
  const isAutoTranslatingCatDesc = ref(false);

  let newColTimer: ReturnType<typeof setTimeout> | null = null;
  let catDesigTimer: ReturnType<typeof setTimeout> | null = null;
  let catDescTimer: ReturnType<typeof setTimeout> | null = null;

  const collectionsByCategory = computed(() => {
    const products: any[] = dataGetProduct.value?.data ?? [];
    const map: Record<string, any[]> = {};
    products.forEach((p: any) => {
      const cats: any[] = p.categories ?? [];
      cats.forEach((c: any) => {
        const cat = c?.category?.designation;
        if (cat) {
          if (!map[cat]) map[cat] = [];
          map[cat].push(p);
        }
      });
    });
    return map;
  });

  const allCollections = computed(() => {
    const merge = (name: string, base: any[]) => {
      const extras = (droppedProducts.value[name] ?? []).filter(
        (p: any) => !base.some((b: any) => b.id === p.id),
      );
      return [...base, ...extras];
    };
    const apiCats: any[] = (dataGetCategories.value as any)?.data ?? [];
    let orderedCats = apiCats;
    if (localCategoryOrder.value) {
      const catById = new Map(apiCats.map((c: any) => [c.id, c]));
      const reordered: any[] = localCategoryOrder.value
        .map((id) => catById.get(id))
        .filter(Boolean);
      const seen = new Set(localCategoryOrder.value);
      apiCats.forEach((c: any) => {
        if (!seen.has(c.id)) reordered.push(c);
      });
      orderedCats = reordered;
    }
    const apiOrdered = orderedCats
      .filter(
        (c: any) =>
          c.designation && !deletedCollections.value.includes(c.designation),
      )
      .map((c: any) => ({
        id: c.id as string,
        name: c.designation,
        products: merge(
          c.designation,
          collectionsByCategory.value[c.designation] ?? [],
        ),
      }));
    const apiOrderedNames = new Set(apiOrdered.map((c) => c.name));
    const local = localCollections.value
      .filter(
        (c) =>
          !deletedCollections.value.includes(c.name) &&
          !apiOrderedNames.has(c.name),
      )
      .map((c) => ({
        id: null as string | null,
        name: c.name,
        products: merge(c.name, c.products),
      }));
    return [...apiOrdered, ...local];
  });

  const isFiltering = computed(() => category_product_filter.value.length > 0);

  const visibleCollections = computed(() => {
    if (!isFiltering.value) return allCollections.value;
    return allCollections.value.filter((col) => col.products.length > 0);
  });

  watch(
    allCollections,
    (cols) => {
      if (cols.length > 0) {
        showCollections.value = true;
        if (expandedSections.value.length === 0)
          expandedSections.value = [cols[0].id ?? cols[0].name];
      }
    },
    { immediate: true },
  );

  const toggleSection = (name: string) => {
    const idx = expandedSections.value.indexOf(name);
    if (idx >= 0) expandedSections.value.splice(idx, 1);
    else expandedSections.value.push(name);
  };

  const isSectionExpanded = (name: string) =>
    expandedSections.value.includes(name);

  const deleteCollection = (catId: string | null, name: string) => {
    confirmDeleteName.value = name;
    confirmDeleteId.value = catId;
  };

  const confirmDeleteCollection = () => {
    const name = confirmDeleteName.value;
    const catId = confirmDeleteId.value;
    if (!name) return;
    if (catId) {
      idToDelete.value = catId;
      handleDeleteCategorie();
      setTimeout(() => refetchBoth(), 300);
    }
    deletedCollections.value.push(name);
    const li = localCollections.value.findIndex((c) => c.name === name);
    if (li >= 0) localCollections.value.splice(li, 1);
    confirmDeleteName.value = null;
    confirmDeleteId.value = null;
  };

  const cancelDeleteCollection = () => {
    confirmDeleteName.value = null;
    confirmDeleteId.value = null;
  };

  const startEdit = (catId: string | null, name: string) => {
    editingSection.value = catId ?? name;
    editingName.value = name;
    editCatLang.value = 'fr';
    const cats: any[] = (dataGetCategories.value as any)?.data ?? [];
    const cat = catId
      ? cats.find((c: any) => c.id === catId)
      : cats.find((c: any) => c.designation === name);
    editCatDesigFr.value =
      cat?.designationAlternatives?.fr ?? cat?.designation ?? name;
    editCatDesigNl.value =
      cat?.designationAlternatives?.nl ?? cat?.designation ?? name;
    editCatDesigEn.value =
      cat?.designationAlternatives?.en ?? cat?.designation ?? name;
    editCatDescFr.value =
      cat?.descriptionAlternatives?.fr ?? cat?.description ?? '';
    editCatDescNl.value =
      cat?.descriptionAlternatives?.nl ?? cat?.description ?? '';
    editCatDescEn.value =
      cat?.descriptionAlternatives?.en ?? cat?.description ?? '';
    editCatModules.value = Array.isArray(cat?.modules) ? [...cat.modules] : [];
  };

  const cancelEdit = () => {
    editingSection.value = null;
    editingName.value = '';
  };

  const saveEdit = async (catId: string | null, oldName: string) => {
    const newNameFr = editCatDesigFr.value.trim();
    if (!newNameFr || isSavingCatEdit.value) return;
    isSavingCatEdit.value = true;
    try {
      if (catId) {
        idCategory.value = catId;
        await handleUpdateCategoryById({
          designation: {
            fr: editCatDesigFr.value,
            nl: editCatDesigNl.value,
            en: editCatDesigEn.value,
          },
          description: {
            fr: editCatDescFr.value,
            nl: editCatDescNl.value,
            en: editCatDescEn.value,
          },
          modules: editCatModules.value,
        });
        refetchBoth();
        await refetchCategories();
      } else {
        const li = localCollections.value.find((c) => c.name === oldName);
        if (li) li.name = newNameFr;
      }
      showSuccessToast('Catégorie mise à jour');
    } catch {
      showErrorToast('Erreur lors de la mise à jour de la catégorie');
    } finally {
      isSavingCatEdit.value = false;
      editingSection.value = null;
      editingName.value = '';
    }
  };

  const scheduleCatDesigTranslation = (text: string) => {
    if (catDesigTimer) clearTimeout(catDesigTimer);
    if (!text.trim()) return;
    catDesigTimer = setTimeout(async () => {
      isAutoTranslatingCatDesig.value = true;
      try {
        await runTranslation(
          text,
          (code, t) => {
            if (code === 'fr') editCatDesigFr.value = t;
            if (code === 'nl') editCatDesigNl.value = t;
            if (code === 'en') editCatDesigEn.value = t;
          },
          editCatLang.value,
        );
      } catch {
        /* silent */
      }
      isAutoTranslatingCatDesig.value = false;
    }, 700);
  };

  const scheduleCatDescTranslation = (text: string) => {
    if (catDescTimer) clearTimeout(catDescTimer);
    if (!text.trim()) return;
    catDescTimer = setTimeout(async () => {
      isAutoTranslatingCatDesc.value = true;
      try {
        await runTranslation(
          text,
          (code, t) => {
            if (code === 'fr') editCatDescFr.value = t;
            if (code === 'nl') editCatDescNl.value = t;
            if (code === 'en') editCatDescEn.value = t;
          },
          editCatLang.value,
        );
      } catch {
        /* silent */
      }
      isAutoTranslatingCatDesc.value = false;
    }, 700);
  };

  const onCatDesigInput = (lang: 'fr' | 'nl' | 'en', val: string) => {
    if (lang === editCatLang.value) scheduleCatDesigTranslation(val);
  };
  const onCatDescInput = (lang: 'fr' | 'nl' | 'en', val: string) => {
    if (lang === editCatLang.value) scheduleCatDescTranslation(val);
  };
  const scheduleNewColTranslation = (text: string) => {
    if (newColTimer) clearTimeout(newColTimer);
    if (!text.trim()) return;
    newColTimer = setTimeout(async () => {
      isAutoTranslatingNewCol.value = true;
      try {
        await runTranslation(
          text,
          (code, t) => {
            if (code === 'fr') newColFr.value = t;
            if (code === 'nl') newColNl.value = t;
            if (code === 'en') newColEn.value = t;
          },
          newColLang.value,
        );
      } catch {
        /* silent */
      }
      isAutoTranslatingNewCol.value = false;
    }, 700);
  };

  const onNewColInput = (lang: 'fr' | 'nl' | 'en', val: string) => {
    if (lang === newColLang.value) scheduleNewColTranslation(val);
  };
  const confirmAdd = async () => {
    const nameFr = (newColFr.value || newCollectionName.value).trim();
    if (!nameFr || isAddingCollection.value) return;
    isAddingCollection.value = true;
    try {
      await handleCreateCategory({
        designation: {
          fr: newColFr.value.trim() || nameFr,
          nl: newColNl.value.trim() || nameFr,
          en: newColEn.value.trim() || nameFr,
        },
        description: {
          fr: newColFr.value.trim() || nameFr,
          nl: newColNl.value.trim() || nameFr,
          en: newColEn.value.trim() || nameFr,
        },
        organizationId: organizationIdCommerce.value,
        modules: newColModules.value,
      });
      refetchBoth();
      await refetchCategories();
      if (
        !collectionsByCategory.value[nameFr] &&
        !localCollections.value.some((c) => c.name === nameFr)
      ) {
        localCollections.value.push({ name: nameFr, products: [] });
      }
      expandedSections.value.push(nameFr);
      newColFr.value = '';
      newColNl.value = '';
      newColEn.value = '';
      newColModules.value = [];
      newCollectionName.value = '';
      showAddInput.value = false;
    } finally {
      isAddingCollection.value = false;
    }
  };

  const cancelAdd = () => {
    newCollectionName.value = '';
    newColFr.value = '';
    newColNl.value = '';
    newColEn.value = '';
    newColModules.value = [];
    showAddInput.value = false;
  };
  const onDragStart = (product: any) => {
    draggingProduct.value = product;
  };
  const onDragOver = (colKey: string) => {
    dragOverCollection.value = colKey;
  };
  const onDragLeave = () => {
    dragOverCollection.value = null;
  };

  const onDrop = async (colId: string | null, colName: string) => {
    const product = draggingProduct.value;
    if (!product) return;
    const existing = droppedProducts.value[colName] ?? [];
    const alreadyIn = existing.some((p: any) => p.id === product.id);
    if (!alreadyIn) {
      droppedProducts.value = {
        ...droppedProducts.value,
        [colName]: [...existing, product],
      };
      if (colId) {
        try {
          const full = (await productStore.getProductById(product.id)) as any;
          const p = full?.data ?? full;
          const existingCategoryIds: string[] = (p?.categories ?? [])
            .map((c: any) => c?.category?.id ?? c?.id)
            .filter(Boolean);
          await productStore.updateProduct(product.id, {
            addInCategories: [...new Set([...existingCategoryIds, colId])],
          });
          refetchBoth();
          showSuccessToast('Produit ajouté à la collection avec succès');
        } catch {
          droppedProducts.value = {
            ...droppedProducts.value,
            [colName]: (droppedProducts.value[colName] ?? []).filter(
              (p: any) => p.id !== product.id,
            ),
          };
          showErrorToast("Erreur lors de l'ajout du produit à la collection");
        }
      }
    }
    draggingProduct.value = null;
    dragOverCollection.value = null;
    const colKey = colId ?? colName;
    if (!expandedSections.value.includes(colKey))
      expandedSections.value.push(colKey);
  };

  const removeProductFromCategory = async (
    productId: string,
    catId: string | null,
  ) => {
    if (!productId || !catId || deletingProductId.value === productId) return;
    deletingProductId.value = productId;
    try {
      await productStore.updateProduct(productId, {
        removeFromCategories: [catId],
      });
      refetchBoth();
    } catch {
      showErrorToast('Erreur lors du retrait du produit');
    } finally {
      deletingProductId.value = null;
    }
  };

  const applyOptimisticSwap = (idA: string, idB: string) => {
    const apiCats: any[] = (dataGetCategories.value as any)?.data ?? [];
    const currentOrder = (localCategoryOrder.value ??
      apiCats.map((c: any) => c.id)) as string[];
    const order = [...currentOrder];
    const idxA = order.indexOf(idA);
    const idxB = order.indexOf(idB);
    if (idxA >= 0 && idxB >= 0)
      [order[idxA], order[idxB]] = [order[idxB], order[idxA]];
    localCategoryOrder.value = order;
  };

  const moveCategoryUp = async (index: number) => {
    const cats = visibleCollections.value;
    if (index <= 0 || movingCategoryId.value) return;
    const catId = cats[index].id;
    const prevId = cats[index - 1].id;
    if (!catId || !prevId) return;
    applyOptimisticSwap(catId, prevId);
    movingCategoryId.value = catId;
    try {
      const afterId = index >= 2 ? (cats[index - 2].id ?? null) : null;
      await axiosInstance.put(
        `products/categories/move/${catId}`,
        { afterId },
        { headers: { 'x-client-app': 'admin-back-office' } },
      );
      await refetchCategories();
    } catch {
      localCategoryOrder.value = null;
      showErrorToast('Erreur lors du déplacement de la catégorie');
    } finally {
      localCategoryOrder.value = null;
      movingCategoryId.value = null;
    }
  };

  const moveCategoryDown = async (index: number) => {
    const cats = visibleCollections.value;
    if (index >= cats.length - 1 || movingCategoryId.value) return;
    const catId = cats[index].id;
    const nextId = cats[index + 1].id;
    if (!catId || !nextId) return;
    applyOptimisticSwap(catId, nextId);
    movingCategoryId.value = catId;
    try {
      await axiosInstance.put(
        `products/categories/move/${catId}`,
        { afterId: nextId },
        { headers: { 'x-client-app': 'admin-back-office' } },
      );
      await refetchCategories();
    } catch {
      localCategoryOrder.value = null;
      showErrorToast('Erreur lors du déplacement de la catégorie');
    } finally {
      localCategoryOrder.value = null;
      movingCategoryId.value = null;
    }
  };

  const resetCollectionsState = () => {
    showCollections.value = allCollections.value.length > 0;
    const first = allCollections.value[0];
    expandedSections.value = first ? [first.id ?? first.name] : [];
    deletedCollections.value = [];
    localCollections.value = [];
    editingSection.value = null;
    showAddInput.value = false;
    newCollectionName.value = '';
    newColFr.value = '';
    newColNl.value = '';
    newColEn.value = '';
    newColModules.value = [];
    draggingProduct.value = null;
    dragOverCollection.value = null;
    droppedProducts.value = {};
  };

  return {
    deletedCollections,
    localCollections,
    editingSection,
    editingName,
    showAddInput,
    newCollectionName,
    newColFr,
    newColNl,
    newColEn,
    newColLang,
    newColModules,
    isAutoTranslatingNewCol,
    confirmDeleteName,
    confirmDeleteId,
    draggingProduct,
    dragOverCollection,
    droppedProducts,
    expandedSections,
    showCollections,
    localCategoryOrder,
    movingCategoryId,
    deletingProductId,
    isAddingCollection,
    editCatLang,
    editCatDesigFr,
    editCatDesigNl,
    editCatDesigEn,
    editCatDescFr,
    editCatDescNl,
    editCatDescEn,
    editCatModules,
    isSavingCatEdit,
    isAutoTranslatingCatDesig,
    isAutoTranslatingCatDesc,
    collectionsByCategory,
    allCollections,
    isFiltering,
    visibleCollections,
    toggleSection,
    isSectionExpanded,
    deleteCollection,
    confirmDeleteCollection,
    cancelDeleteCollection,
    startEdit,
    cancelEdit,
    saveEdit,
    onCatDesigInput,
    onCatDescInput,
    onNewColInput,
    confirmAdd,
    cancelAdd,
    onDragStart,
    onDragOver,
    onDragLeave,
    onDrop,
    removeProductFromCategory,
    applyOptimisticSwap,
    moveCategoryUp,
    moveCategoryDown,
    resetCollectionsState,
  };
};
